import { createTypedRoute, RouteExtras } from '@app/shared/utils/route-creators/create-typed-route';
import { RequiredRouterDataForErrorComponent } from '@app/error-page/error-page.component';

/**
 * @function createErrorPageRoute
 * @description
 * Creates a typed route for the generic error page component.
 * This can be used for various error scenarios such as 404 Not Found,
 * 500 Server Error, 403 Forbidden, or any custom error page.
 *
 * @param path - The route path (e.g., 'not-found', 'server-error', 'forbidden')
 * @param data - Strongly typed error page data containing:
 *   - errorCode: HTTP status code or custom error identifier
 *   - title: Error page heading
 *   - details: Descriptive error message for the user
 *   - message: Optional additional context
 * @param extras - Optional route configuration (guards, resolvers, etc.)
 *
 * @returns Angular route with typed error data
 *
 * @example
 * // 404 Not Found
 * createErrorPageRoute('not-found', {
 *   errorCode: 404,
 *   title: 'Page Not Found',
 *   details: 'The page you are looking for does not exist.'
 * })
 *
 * @example
 * // 500 Server Error
 * createErrorPageRoute('server-error', {
 *   errorCode: 500,
 *   title: 'System Error',
 *   details: 'A critical error occurred. We are investigating.'
 * })
 *
 * @example
 * // 403 Forbidden with route guard
 * createErrorPageRoute('forbidden', {
 *   errorCode: 403,
 *   title: 'Access Restricted',
 *   details: 'You do not have permission to access this resource.'
 * }, {
 *   canActivate: [AuthGuard]
 * })
 */
export const createErrorRoute = ({
  path,
  data,
  extras
} : {
  path: string,
  data: RequiredRouterDataForErrorComponent,
  extras?: RouteExtras
}) => {
  return createTypedRoute<RequiredRouterDataForErrorComponent>({
    path,
    loadComponent: () => import('@app/error-page/error-page.component'),
    data,
    ...(extras && { extras })
  });
};